import React, { useMemo } from 'react';
import {
  useScenarioOrTotalProgramExportable,
  useWorksheet,
} from 'spw-programming-projects/downloads/exportables/hooks';
import {
  WorksheetNote,
  TotalProgramWorksheetNotes,
} from 'spw-programming-projects/program-summary/program-summary-tabs/summary-notes';
import { groupBy } from 'ramda';
import { ICategoryNote } from 'spw-redux/modules/calculators/types';
import { useLocation } from 'react-router-dom-v5';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { useSelector } from 'react-redux';
import { projectColors as selectProjectColors } from 'spw-redux/modules/projects/selectors';
import { isDepartmentScenario } from 'spw-components/worksheet/utils';
import { getWorksheetsByScenarioIdMap } from 'spw-components/programming-scenarios/utils';
import {
  GENERAL_CATEGORY_TITLE,
  NOTE_GENERAL_CATEGORY,
  SHARED_SPACES_CATEGORY,
  TITLES,
} from 'spw-constants';

const NotesExportable: React.FunctionComponent = () => {
  const {
    isTotalProgram,
    currentScenario,
    currentProject,
    departments,
  } = useScenarioOrTotalProgramExportable();
  const location = useLocation();
  const urlParams = new URLSearchParams(location?.search);
  const category = urlParams.get('category');
  const { worksheet, tpsWorksheets } = useWorksheet(isTotalProgram);
  const currentProjectColors = useSelector(selectProjectColors);

  const notesByCategory: { [key: string]: any } = useMemo(() => {
    return groupBy((note: ICategoryNote) => note.category)(
      worksheet?.notes || []
    );
  }, [worksheet]);

  const projectSpaceTypeCategory = useMemo(() => {
    return currentProject?.spaceTypeCategories?.find(
      (currentProjectSpaceType: IProjectSpaceTypeCategory) => {
        return (
          currentProjectSpaceType.defaultSpaceTypeCategory.category === category
        );
      }
    );
  }, [currentProject, category]);

  const notesCategory = useMemo(() => {
    if (category === NOTE_GENERAL_CATEGORY) {
      return {
        name: GENERAL_CATEGORY_TITLE,
        key: NOTE_GENERAL_CATEGORY,
      };
    }

    if (category === SHARED_SPACES_CATEGORY) {
      return {
        name: TITLES.SHARED_SPACES,
        key: SHARED_SPACES_CATEGORY,
      };
    }

    return {
      name: projectSpaceTypeCategory?.name,
      key: category,
    };
  }, [category, projectSpaceTypeCategory]);

  const shouldRender = useMemo(() => {
    let isAllDataAvailable = Boolean(
      currentScenario?.id &&
        currentProject?.id &&
        worksheet &&
        (projectSpaceTypeCategory ||
          [NOTE_GENERAL_CATEGORY, SHARED_SPACES_CATEGORY].includes(category))
    );

    if (isDepartmentScenario(currentScenario)) {
      isAllDataAvailable = Boolean(isAllDataAvailable && departments.length);
    }

    return isAllDataAvailable;
  }, [
    currentScenario,
    currentProject?.id,
    worksheet,
    departments,
    projectSpaceTypeCategory,
    category,
  ]);

  const scenarioFloor = currentScenario?.building?.floors?.find(
    (floor) => floor.id === currentScenario.floorId
  );

  return shouldRender ? (
    <div
      className="content"
      style={{ backgroundColor: 'white', overflow: 'hidden' }}
    >
      {isTotalProgram ? (
        <TotalProgramWorksheetNotes
          totalProgram={currentScenario}
          category={notesCategory}
          scenarioWorksheets={getWorksheetsByScenarioIdMap(tpsWorksheets)}
          projectColors={currentProjectColors}
          isRenderingForExport
        />
      ) : (
        <WorksheetNote
          notesByCategory={notesByCategory}
          category={notesCategory}
          scenario={currentScenario}
          floor={scenarioFloor}
          department={currentScenario.department}
          projectColors={currentProjectColors}
          isRenderingForExport
        />
      )}
    </div>
  ) : null;
};

export default NotesExportable;
